import { getPersistedLanguage } from "@/lib/language-storage"
import { getMessageContent } from "@/modules/chats/lib/format-chat-content"
import { useChatMessagesQuery$data } from "@/modules/chats/services/__generated__/useChatMessagesQuery.graphql"
import {
  AiFeatureKind,
  ChatMessageKind,
  ChatMessageRole,
  ChatMetadata,
  SofiaChatMessage,
} from "@/modules/chats/types/chat-message"

type ChatMessageEdges = NonNullable<NonNullable<useChatMessagesQuery$data["chatMessages"]>["edges"]>
type ChatMessageNode = NonNullable<ChatMessageEdges[number]["node"]>

const deriveRole = (type: string, senderUserName: string | null | undefined): ChatMessageRole => {
  if (type !== "text") {
    return "system"
  }
  return senderUserName ? "doctor" : "patient"
}

export const parseAiFeatures = (raw: unknown): AiFeatureKind[] => {
  if (typeof raw !== "string" || !raw) return []

  try {
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is AiFeatureKind => typeof item === "string")
    }
  } catch {
    // raw is not valid JSON — not a valid AiFeatureKind array, so return empty.
  }

  return []
}

const toChatMessageKind = (raw: string): ChatMessageKind => {
  switch (raw) {
    case "text":
    case "participantAdded":
    case "participantRemoved":
      return raw
    default:
      return "text"
  }
}

const buildMetadata = (raw: ChatMessageNode["metadata"]): ChatMetadata | undefined => {
  if (!raw) return undefined

  const aiFeatures = parseAiFeatures(raw.aiFeatures)

  return {
    originalContent: raw.originalContent ?? undefined,
    aiFeatures: aiFeatures.length > 0 ? aiFeatures : undefined,
    autoTranslationOriginalContent: raw.autoTranslationOriginalContent ?? undefined,
    autoTranslationTranslatedContent: raw.autoTranslationTranslatedContent ?? undefined,
    autoTranslationBackTranslatedContent: raw.autoTranslationBackTranslatedContent ?? undefined,
    autoTranslationTargetLanguage: raw.autoTranslationTargetLanguage ?? undefined,
    autoTranslationFailed: raw.autoTranslationFailed ?? undefined,
  }
}

// Participant events carry no message body from ACS; the join/left text must be synthesized
// client-side from the participant's name, the event type and the timestamp.
const buildContent = (node: ChatMessageNode): string => {
  if (node.type === "participantAdded" || node.type === "participantRemoved") {
    const displayName = node.participants?.[0]?.displayName ?? node.senderDisplayName ?? ""
    return getMessageContent(node.type, "", new Date(node.createdOn), displayName, getPersistedLanguage())
  }
  return node.content ?? ""
}

const mapChatMessageNode = (node: ChatMessageNode): SofiaChatMessage => {
  const sentAt = new Date(node.createdOn)
  const metadata = buildMetadata(node.metadata)
  const role = deriveRole(node.type, node.metadata?.senderUserName)

  return {
    id: node.id,
    sentAt,
    content: buildContent(node),
    senderDisplayName: node.senderDisplayName ?? "",
    role,
    type: toChatMessageKind(node.type),
    acsSenderIdentity: node.senderId ?? null,
    acsChatThreadId: "",
    metadata,
  }
}

const isThreadCreationParticipantEvent = (node: ChatMessageNode): boolean =>
  node.type === "participantAdded" && (node.participants?.length ?? 0) > 1

const isEmptySystemEvent = (node: ChatMessageNode): boolean =>
  deriveRole(node.type, node.metadata?.senderUserName) === "system" && !buildContent(node).trim()

const mapChatMessageEdges = (edges: ChatMessageEdges): SofiaChatMessage[] => {
  const result: SofiaChatMessage[] = []
  const seenIds = new Set<string>()
  for (const { node } of edges) {
    if (!node) continue
    if (seenIds.has(node.id)) continue
    if (isThreadCreationParticipantEvent(node)) continue
    if (isEmptySystemEvent(node)) continue
    seenIds.add(node.id)
    result.push(mapChatMessageNode(node))
  }
  return result.reverse()
}

export { mapChatMessageEdges, mapChatMessageNode }
